import { EpaGridOrderByFetchParams } from "../../leap-components/Grid/EpaGridProps";
import { API_URL } from "./Config";
import { FetchResponse } from ".././FetchResponse";

export interface INotifier {
  notifier_id: string;
  notifier_name: string;
  date_received: string;
  date_closed: string;
  notified_material: string;
  reg_no: string;
  status: string;
  open_for_submissions: boolean;
  notified_material_description: string;
  quantity: number;
  greenfield_or_brownfield: string;
  material_production_process: string;
  activities_taken: string;
  specific_use: string;
  source_county: string;
  source_name_and_address: string;
  source_eircode: string;
  source_eastings: string;
  source_northings: string;
  source_local_authority: string;
  use_county: string;
  use_name_and_address: string;
  use_eir_code: string;
  use_eastings: string;
  use_northings: string;
  use_local_authority: string;
  planning_status: string;
  planning_refno: string;
  planning_grant_ref_date: string;
  planning_expiry_date: string;
  planning_quantity_authorised: number;
  environmental_authorisation: string;
  environmental_authorisation_details: string;
  type: string;
  decision_ref_no: string;
  hyperlink: string;
  waste_authorisation_type: string;
  epa_waste_authorisation: string;
  local_authority_id: string;
  waste_authorisation_regno: string;
  waste_authorisation_expiry_date: string;
  licence_type: string;
}

export interface INotifierDetails {
  notifier_id: string;
  notifier_name: string;
  date_received: string;
  reg_no: string;
  status: string;
  open_for_submissions: boolean;
  source_local_authority: string;
  use_local_authority: string;
}

export interface INotifierResponse {
  response: FetchResponse;
  notifier?: INotifierDetails;
}

export async function GetNotifierByIdFetchUrl(
  notifier_id: string
): Promise<INotifierResponse> {
  var url = `${API_URL}/Notifiers/byid?notifier_id=${notifier_id}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/notifier`;
  }

  var response = await fetch(url);

  if (!response.ok) {
    return {
      response: {
        ok: false,
        httpStatusCode: response.status,
        httpErr: response.statusText,
      },
    };
  }

  var body = <INotifierDetails>await response.json();

  const notifier = <INotifierDetails>{
    notifier_id: body.notifier_id,
    notifier_name: body.notifier_name,
    date_received: body.date_received,
    reg_no: body.reg_no,
    status: body.status,
    source_local_authority: body.source_local_authority,
    use_local_authority: body.use_local_authority,
    open_for_submissions: body.open_for_submissions,
  };

  return <INotifierResponse>{
    response: {},
    notifier: notifier,
  };
}

export const GetNotifiersFetchUrl = (
  pageNo: number,
  recordsPerPage: number,
  filters: any
) => {
  let params = `?page=${pageNo}&per_page=${recordsPerPage}`;

  let url = `${API_URL}/Notifiers${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/notifiers`;
  }

  return url;
};

export const GetFilteredNotifiersFetchUrl = (
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any,
  orderBy?: EpaGridOrderByFetchParams
) => {
  let params = `?page=${pageNo}&per_page=${recordsPerPage}`;

  if (filters) {
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      let filterId = f.id;

      if (f.value != "All") params += `&${filterId}=${f.value}`;
    }
  }

  if (searchText.length > 0) {
    params += `&search_term=${encodeURIComponent(searchText)}`;
  }

  if (orderBy) {
    let orderByParam = "";
    switch (orderBy?.orderByName) {
      case "Date Closed":
        orderByParam = "date_closed";
        break;

      case "Date Received":
        orderByParam = "date_received";
        break;

      case "Reg Number":
      default:
        orderByParam = "notification_number";
    }

    params += `&order_by=${encodeURIComponent(orderByParam)}&ascending=${
      orderBy.orderByAscending ? "true" : "false"
    }`;
  }

  let url = `${API_URL}/Notifiers/filteredregistrationslist${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/notifiers`;
  }

  return url;
};

export const GetFilteredNotifiersCsv = (
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any,
  orderBy?: EpaGridOrderByFetchParams
) => {
  let params = `?page=${pageNo}&per_page=${recordsPerPage}`;

  if (filters) {
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      let filterId = f.id;
      params += `&${filterId}=${f.value}`;
    }
  }

  if (searchText.length > 0) {
    params += `&search_term=${encodeURIComponent(searchText)}`;
  }

  if (orderBy) {
    let orderByParam = "";
    switch (orderBy?.orderByName) {
      case "Date Closed":
        orderByParam = "date_closed";
        break;

      case "Date Received":
        orderByParam = "date_received";
        break;

      case "Reg Number":
      default:
        orderByParam = "notification_number";
    }

    params += `&order_by=${encodeURIComponent(orderByParam)}&ascending=${
      orderBy.orderByAscending ? "true" : "false"
    }`;
  }

  let url = `${API_URL}/Notifiers/filteredresitrationscsv${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/notifiers`;
  }

  return url;
};

export const GetFilteredEowsFetchUrl = (
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any,
  orderBy?: EpaGridOrderByFetchParams
) => {
  let params = `?page=${pageNo}&per_page=${recordsPerPage}`;

  if (filters) {
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      let filterId = f.id;

      if (f.value != "All") params += `&${filterId}=${f.value}`;
    }
  }

  if (searchText.length > 0) {
    params += `&search_term=${encodeURIComponent(searchText)}`;
  }

  if (orderBy) {
    let orderByParam = "";
    switch (orderBy?.orderByName) {
      case "Registration Date":
        orderByParam = "date_received";
        break;

      case "Operator":
        orderByParam = "notifier_name";
        break;

      case "Reg Number":
      default:
        orderByParam = "notification_number";
    }

    params += `&order_by=${encodeURIComponent(orderByParam)}&ascending=${
      orderBy.orderByAscending ? "true" : "false"
    }`;
  }

  let url = `${API_URL}/Notifiers/filteredeowlist${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/eows`;
  }

  return url;
};

export const GetFilteredEowsCsv = (
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any,
  orderBy?: EpaGridOrderByFetchParams
) => {
  let params = `?page=${pageNo}&per_page=${recordsPerPage}`;

  if (filters) {
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      let filterId = f.id;
      params += `&${filterId}=${f.value}`;
    }
  }

  if (searchText.length > 0) {
    params += `&search_term=${encodeURIComponent(searchText)}`;
  }

  if (orderBy) {
    let orderByParam = "";
    switch (orderBy?.orderByName) {
      case "Registration Date":
        orderByParam = "date_received";
        break;

      case "Operator":
        orderByParam = "notifier_name";
        break;

      case "Reg Number":
      default:
        orderByParam = "notification_number";
    }

    params += `&order_by=${encodeURIComponent(orderByParam)}&ascending=${
      orderBy.orderByAscending ? "true" : "false"
    }`;
  }

  let url = `${API_URL}/Notifiers/filteredeowcsv${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/eows`;
  }

  return url;
};

export const GetFilteredDocumentsFetchUrl = (
  notifierId: string,
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any
) => {
  let params = `?notifier_id=${notifierId}&page=${pageNo}&per_page=${recordsPerPage}`;

  if (filters) {
    for (var i = 0; i < filters.length; i++) {
      var f = filters[i];
      let filterId = f.id;
      params += `&${filterId}=${f.value}`;
    }
  }

  if (searchText.length > 0) {
    params += `&search_term=${encodeURIComponent(searchText)}`;
  }

  let url = `${API_URL}/Documents/bynotifier${params}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/documents`;
  }

  return url;
};
